import type { JSX, ReactNode } from 'react';
import { ErrorState } from '@/components/feedback';

type ErrorRenderer = ReactNode | ((error: Error) => ReactNode);

const defaultEmptyMessage = 'There is no data to display right now. Please check back later.';

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  return new Error('Unknown error');
};

export interface AsyncStateProps<Data> {
  data: Data | null | undefined;
  isLoading: boolean;
  error: unknown;
  skeleton: ReactNode;
  children: (value: Data) => ReactNode;
  errorFallback?: ErrorRenderer;
  emptyFallback?: ReactNode;
}

/**
 * Declaratively render async UI states in the correct order: loading → error → empty → success.
 */
export function AsyncState<Data>({
  data,
  isLoading,
  error,
  skeleton,
  children,
  errorFallback,
  emptyFallback,
}: AsyncStateProps<Data>): JSX.Element {
  if (isLoading) {
    return <>{skeleton}</>;
  }

  if (error !== null && error !== undefined) {
    const normalized = normalizeError(error);
    if (typeof errorFallback === 'function') {
      return <>{errorFallback(normalized)}</>;
    }
    if (errorFallback !== null && errorFallback !== undefined) {
      return <>{errorFallback}</>;
    }
    return (
      <ErrorState description={normalized.message || 'We were unable to load this content.'} />
    );
  }

  if (data === null || data === undefined) {
    if (emptyFallback !== null && emptyFallback !== undefined) {
      return <>{emptyFallback}</>;
    }
    return <ErrorState description={defaultEmptyMessage} />;
  }

  return <>{children(data)}</>;
}
